import React, {useEffect, useRef, useState} from "react";
import HTMLFlipBook from "react-pageflip";
import {useParams} from "react-router-dom";
import {getRefreshToken, getTokenFromSession, refreshIdToken} from "../../services/authenticate";
import axiosInstance from "../../axiosInstance";
import config from "../../Config/config";
import "../../css/storyViewer.css";
import HeaderComponent from "../HeaderComponent";
import Breadcrumb from "../../wrapper/Breadcrumb";
import Footer from "../../assets/Footer";

const Page = React.forwardRef(({title, content, index, image}, ref) => (
    <div className="demoPage" ref={ref}>
        <div className="page-content" style={{backgroundColor: "#edf2f7"}}>
            {title && (
                <div className="h2-content">
                    <h2>{title}</h2>
                </div>
            )}
            {content && <p>{content}</p>}
            {image && <img src={image} alt={title}/>}
        </div>
    </div>
));

const StoryViewer = ({searchTerm, setSearchTerm, genres}) => {
    const {storyId} = useParams();
    const [currentPage, setCurrentPage] = useState(0);
    const [pages, setPages] = useState([]);
    const [audio, setAudio] = useState([]);
    const bookRef = useRef();
    const audioRef = useRef(null);
    const audioPageRef = useRef(0); // ✅ useRef instead of useState
    const audioPlayingRef = useRef(false);
    const previousPageRef = useRef(0);

    const flipToNext = () => {
        bookRef.current.pageFlip().flipNext();
    };

    const flipToPrevious = () => {
        bookRef.current.pageFlip().flipPrev();
    };

    useEffect(() => {
        fetchStory();
    }, [storyId]);

    const fetchStory = async () => {
        const token = getTokenFromSession();
        try {
            const response = await axiosInstance.get(`${config.api.story}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${token}`,
                },
                params: {storyId},
            });

            const data = response.data;
            const fetchedPages = [];
            const fetchedAudio = [];

            data.forEach((item) => {
                if (item.title) fetchedPages.push({title: item.title});
                if (item.image) fetchedPages.push({image: item.image});
                if (item.content) fetchedPages.push({content: item.content});
                if (item.audio) fetchedAudio.push({audio: item.audio});
            });

            setPages(fetchedPages);
            setAudio(fetchedAudio);
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                const refreshToken = getRefreshToken();
                await refreshIdToken(refreshToken);
                await fetchStory();
            } else {
                console.error("Fetch story error:", error);
            }
        }
    };

    const handlePlay = () => {
        audioPlayingRef.current = true;
        if (currentPage === 0) {
            flipToNext();
        } else {
            audioRef.current.play();
        }
    };

    const handleAudioEnded = () => {
        audioPlayingRef.current = true;
        flipToNext();
    };

    const handlePause = () => {
        if (audioRef.current) {
            audioPlayingRef.current = false;
            audioRef.current.pause();
        }
    };
    const handlePageFlip = (e) => {
        setCurrentPage(e.data);
        const newPage = e.data;
        const prevPage = previousPageRef.current;
        if (newPage !== 1) {
            if (newPage > prevPage) {
                nextAudio()
            } else if (newPage < prevPage) {
                previousAudio()
            }
        } else {
            audioPageRef.current += 1;
        }
        previousPageRef.current = newPage;
    };

    const nextAudio = () => {
        const newIndex = audioPageRef.current;
        if (audioPlayingRef.current) {
            const currentAudio = audio[newIndex]?.audio;
            if (!currentAudio) {
                console.log("hello")
                audioRef.current.pause();
                audioRef.current.removeAttribute('src');
                audioPageRef.current += 1;
                return;
            }
            if (audioRef.current && currentAudio) {
                audioPageRef.current += 1;
                audioRef.current.pause();
                audioRef.current.removeAttribute('src');
                audioRef.current.load();
                audioRef.current.src = currentAudio;
                audioRef.current.play();
            }
        } else {
            audioPageRef.current += 1;
            audioRef.current.pause();
            audioRef.current.removeAttribute('src');
            audioRef.current.load();
            audioRef.current.src = audio[newIndex]?.audio;
            audioRef.current.pause();
        }
    }

    const previousAudio = () => {
        const newIndex = audioPageRef.current - 2;
        if (audioPlayingRef.current) {
            const currentAudio = audio[newIndex]?.audio;
            if (audioRef.current && currentAudio) {
                audioPageRef.current = audioPageRef.current - 1;
                audioRef.current.pause();
                audioRef.current.removeAttribute('src');
                audioRef.current.load();
                audioRef.current.src = currentAudio;
                audioRef.current.play();
            }
        } else {
            audioPageRef.current = audioPageRef.current - 1;
            audioRef.current.pause();
            audioRef.current.removeAttribute('src');
            audioRef.current.src = audio[newIndex]?.audio;
            audioRef.current.pause();
            audioRef.current.load();
        }
    }
    const [height, setHeight] = useState(280);
    useEffect(() => {
        const updateHeight = () => {
            if (window.matchMedia('(max-width: 480px)').matches) {
                setHeight(400); // phone
            } else if (window.matchMedia('(max-width: 768px)').matches) {
                setHeight(300); // tablet
            } else {
                setHeight(280); // desktop
            }
        };

        updateHeight(); // call on mount
        window.addEventListener('resize', updateHeight);

        return () => window.removeEventListener('resize', updateHeight);
    }, []);
    return (
        <div>
            <HeaderComponent searchTerm={searchTerm} handleSearch={setSearchTerm} genres={genres}/>
            <div style={{marginTop: "200px"}}></div>
            <Breadcrumb
                pages={[
                    {label: "AllStories", path: "/storys"},
                    {label: "Story", path: "/story"},
                ]}
            />
            <div className="main-container">
                <div className="story-content">
                    <HTMLFlipBook
                        width={300}
                        height={height}
                        size="stretch"
                        maxShadowOpacity={0.5}
                        showCover={true}
                        mobileScrollSupport={true}
                        ref={bookRef}
                        onFlip={handlePageFlip}
                        className="demo-book"
                        flippingTime={1000}
                        usePortrait={false}
                    >
                        {pages.map((page, index) => (
                            <Page
                                key={index}
                                title={page.title}
                                content={page.content}
                                index={index}
                                image={page.image}
                            />
                        ))}
                    </HTMLFlipBook>
                </div>
                <div className="audio-container">
                    <audio
                        ref={audioRef}
                        controls={audio.length > 0}
                        onEnded={handleAudioEnded}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        style={{display: audio.length > 0 ? 'block' : 'none'}}
                    >
                        {audio[audioPageRef.current] && (
                            <source src={audio[audioPageRef.current].audio} type="audio/mpeg"/>
                        )}
                    </audio>
                </div>
                <div className="button-container">
                    <button onClick={flipToPrevious} disabled={currentPage === 0} className="previous-button">
                        Previous
                    </button>
                    <button onClick={flipToNext} disabled={currentPage === pages.length - 1} className="next-button">
                        Next
                    </button>
                    {/*<button onClick={handlePlay} className="next-button">*/}
                    {/*    Play Audio*/}
                    {/*</button>*/}
                </div>
            </div>
            <div style={{marginTop: "200px"}}>
                <Footer/>
            </div>
        </div>
    );
};

export default StoryViewer;
